import { useEffect, useMemo, useState } from "react";

import { loadMembers } from "~/lib/matrix";
import type { Client } from "~/lib/matrix";
import { memberKind } from "~/lib/member-kind";
import type { SlashCommand, SlashCtx } from "~/lib/slash-commands";

interface RawManifestEntry {
  name?: unknown;
  description?: unknown;
  args_hint?: unknown;
}

interface RawManifest {
  version?: unknown;
  commands?: unknown;
}

function parseAgentCommands(
  content: unknown,
  agentUserId: string,
  fallbackDisplayName: string,
): SlashCommand[] {
  if (!content || typeof content !== "object") return [];
  const record = content as Record<string, unknown>;
  const rawManifest = record["com.nurion.agent"];
  if (!rawManifest || typeof rawManifest !== "object") return [];
  const manifest = rawManifest as RawManifest;
  if (manifest.version !== 1) return [];
  if (!Array.isArray(manifest.commands)) return [];
  const displayName =
    typeof record.displayname === "string" && record.displayname.length > 0
      ? record.displayname
      : fallbackDisplayName;
  const out: SlashCommand[] = [];
  for (const entry of manifest.commands) {
    if (!entry || typeof entry !== "object") continue;
    const cmd = entry as RawManifestEntry;
    if (typeof cmd.name !== "string" || cmd.name.length === 0) continue;
    if (typeof cmd.description !== "string") continue;
    const argsHint = typeof cmd.args_hint === "string" ? cmd.args_hint : "";
    const name = cmd.name;
    out.push({
      name,
      description: cmd.description,
      usage: `/${name} ${argsHint}`.trim(),
      args: ["text"],
      agent: { userId: agentUserId, displayName },
      handler: (ctx: SlashCtx) => ctx.sendAgentCommand(agentUserId, name, ctx.argsText),
    });
  }
  return out;
}

function commandsKey(list: SlashCommand[]): string {
  return list
    .map(
      (command) =>
        `${command.name}|${command.description}|${command.usage}|${command.agent?.displayName ?? ""}`,
    )
    .sort()
    .join("|");
}

export function useAgentCommands(client: Client | null, roomId: string | null): SlashCommand[] {
  const [commandsByAgent, setCommandsByAgent] = useState<Record<string, SlashCommand[]>>({});

  useEffect(() => {
    if (!client || !roomId) return;
    setCommandsByAgent({});
    let active = true;
    const seen = new Set<string>();
    const unsubscribes: Array<() => void> = [];
    const update = (userId: string, list: SlashCommand[]) => {
      setCommandsByAgent((prev) => {
        const prior = prev[userId];
        if (prior && commandsKey(prior) === commandsKey(list)) return prev;
        return { ...prev, [userId]: list };
      });
    };
    // `getMembers` reads the sync cache, which can be empty on a cold client,
    // leaving an agent without a subscription; `loadMembers` resolves the full
    // roster first so a cached-empty room still subscribes every agent.
    void loadMembers(client, roomId)
      .then((members) => {
        if (!active) return;
        for (const member of members) {
          if (memberKind(member.userId) !== "agent" || seen.has(member.userId)) continue;
          seen.add(member.userId);
          const fallbackDisplayName = member.displayName ?? member.userId;
          const unsubscribe = client.bindings.subscribeRoomStateEvent<unknown>(
            roomId,
            "m.room.member",
            member.userId,
            (content) =>
              update(
                member.userId,
                parseAgentCommands(content, member.userId, fallbackDisplayName),
              ),
          );
          unsubscribes.push(unsubscribe);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [client, roomId]);

  return useMemo(
    () =>
      Object.values(commandsByAgent)
        .flat()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [commandsByAgent],
  );
}
